import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { MdExpandMore, MdOutlineFileDownload } from 'react-icons/md';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { exportMultiSheetToExcel } from '@src/utils/download-utils';
import { toPrettyNumber } from '@src/utils/performance-utils';
import {
  buildCompetitiveCitationExportRows,
  ComparisonVerdict,
  ComparisonVerdictType,
  DrivingQuery,
  FramingSummary
} from '../../alexa-visibility-competitive-utils';

const CITATIONS_PAGE_SIZE = 50;

const FRAMING_LABELS: Record<string, string> = {
  favorable: 'Mostly favorable',
  neutral: 'Mostly neutral',
  unfavorable: 'Mostly unfavorable'
};

const percentOf = (count: number, total: number): number =>
  total > 0 ? Math.round((count / total) * 100) : 0;

interface DrivingQueriesProps {
  queries: DrivingQuery[];
}

export const DrivingQueries = ({ queries }: DrivingQueriesProps) => (
  <div className='aars-cc-card'>
    <div className='aars-cc-card-title'>Queries driving competitive comparisons</div>
    <div className='aars-cc-card-description'>
      Queries where Alexa most frequently makes direct brand comparisons — these are your
      highest-stakes queries
    </div>
    <div className='aars-comp-driving-grid'>
      {queries.map(({ query, comparisons, framing }) => (
        <div key={query} className='aars-comp-driving-card'>
          <div className='aars-comp-driving-query'>{query}</div>
          <div className='aars-comp-driving-meta'>
            <span>{toPrettyNumber(comparisons)} comparisons</span>
            <span className={cn('aars-comp-framing-label', `is-${framing}`)}>
              {FRAMING_LABELS[framing]}
            </span>
          </div>
        </div>
      ))}
      {queries.length === 0 && <span className='aars-cc-empty'>No competitive comparisons</span>}
    </div>
  </div>
);

interface VerdictBannerProps {
  targetBrand: string;
  framing: FramingSummary;
  verdict: ComparisonVerdictType;
}

// Says in a sentence how the brand fares when Alexa compares it with others. Only a winning brand
// gets the positive banner; otherwise the banner says what the numbers actually show.
export const VerdictBanner = ({ targetBrand, framing, verdict }: VerdictBannerProps) => {
  if (framing.total === 0) return null;
  const favorable = percentOf(framing.favorable, framing.total);
  const neutral = percentOf(framing.neutral, framing.total);
  const unfavorable = percentOf(framing.unfavorable, framing.total);
  return (
    <div className={cn('aars-comp-banner', `is-${verdict}`)}>
      {verdict === ComparisonVerdict.WINS && (
        <>
          <div className='aars-comp-banner-title'>
            ✓ {targetBrand} wins most competitive comparisons
          </div>
          <div>
            When Alexa makes a direct comparison, {targetBrand} comes out favorably {favorable}% of
            the time. This is a strong signal that Alexa&apos;s AI model associates {targetBrand}{' '}
            with premium positioning in the category.
          </div>
        </>
      )}
      {verdict === ComparisonVerdict.LOSES && (
        <>
          <div className='aars-comp-banner-title'>
            Competitors win more comparisons than {targetBrand}
          </div>
          <div>
            When Alexa makes a direct comparison, {targetBrand} comes out favorably {favorable}% of
            the time and unfavorably {unfavorable}% of the time.
          </div>
        </>
      )}
      {verdict === ComparisonVerdict.MIXED && (
        <>
          <div className='aars-comp-banner-title'>
            Most comparisons have no clear winner for {targetBrand}
          </div>
          <div>
            When Alexa makes a direct comparison, {targetBrand} comes out favorably {favorable}% of
            the time; {neutral}% of comparisons are neutral.
          </div>
        </>
      )}
    </div>
  );
};

interface CompetitiveCitationsProps {
  targetBrand: string;
  // Every competitive response, newest first.
  comparisons: AlexaVisibilityResult[];
}

export const CompetitiveCitations = ({ targetBrand, comparisons }: CompetitiveCitationsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shown, setShown] = useState(CITATIONS_PAGE_SIZE);
  const [isExporting, setIsExporting] = useState(false);
  const setError = useSetError();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportMultiSheetToExcel(
        [{ name: 'Competitive Citations', data: buildCompetitiveCitationExportRows(comparisons) }],
        `AlexaVisibility_CompetitiveCitations_${targetBrand}`
      );
    } catch (e) {
      setError({
        title: "Couldn't export competitive citations",
        message: `Failed to export competitive citations to Excel: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='aars-chart-card'>
      <div className='aars-chart-card-header'>
        <div className='aars-cc-card-title'>Individual competitive citations</div>
        <div className='aars-comp-citation-actions no-print'>
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            data-html2canvas-ignore='true'
            leadingElement={<MdOutlineFileDownload />}
            onClick={handleExport}
            loading={isExporting}
            disabled={comparisons.length === 0}
          >
            Download all ({toPrettyNumber(comparisons.length)})
          </Button>
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            data-html2canvas-ignore='true'
            trailingElement={
              <MdExpandMore className={cn('aars-comp-caret', { 'is-open': isOpen })} />
            }
            onClick={() => setIsOpen(open => !open)}
            disabled={comparisons.length === 0}
          >
            {isOpen ? 'Hide' : `Show all ${toPrettyNumber(comparisons.length)}`}
          </Button>
        </div>
      </div>
      {isOpen && (
        <div className='aars-comp-citation-list'>
          {comparisons.slice(0, shown).map(result => {
            const framing = result.classification?.competitiveFraming ?? '';
            const rank = result.classification?.targetBrandRank;
            const excerpt = result.classification?.rawExcerpt ?? result.response;
            return (
              <div key={result.id} className='aars-comp-citation'>
                <div className='aars-comp-citation-head'>
                  <span className='aars-comp-citation-query'>{result.query}</span>
                  <span className={cn('aars-comp-framing-label', `is-${framing}`)}>{framing}</span>
                  <span className='aars-comp-citation-meta'>
                    {rank !== undefined ? `Ranked #${rank}` : 'Not ranked'} ·{' '}
                    {format(parseISO(result.createdAt), 'MMM d, yyyy')}
                  </span>
                </div>
                <div className='aars-comp-citation-brands'>
                  Compared: {(result.classification?.brandsMentioned ?? []).join(', ') || '—'}
                </div>
                {excerpt && <blockquote className='aars-cc-excerpt'>“{excerpt}”</blockquote>}
              </div>
            );
          })}
          {shown < comparisons.length && (
            <Button
              size={Size.SM}
              appearance={Appearance.OUTLINED}
              className='no-print'
              onClick={() => setShown(count => count + CITATIONS_PAGE_SIZE)}
            >
              Show {Math.min(CITATIONS_PAGE_SIZE, comparisons.length - shown)} more
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
